import { binColor } from '../../chart/palette';

export type Tone = 'hike' | 'cut' | 'hold';

export interface Toned {
    tone: Tone;
    delta: number;
}

export function modalTones(
    deltas: readonly number[],
    p: readonly (readonly number[])[],
    minShare = 0.5
): Toned[] {
    const floor = Math.round(minShare * 1000);
    const out: Toned[] = new Array(p.length);
    let held: Toned = { tone: 'hold', delta: 0 };

    for (let i = 0; i < p.length; i++) {
        const row = p[i];
        let best = 0;
        for (let j = 1; j < row.length; j++) if (row[j] > row[best]) best = j;

        if (row[best] < floor && i > 0) {
            out[i] = held;
            continue;
        }
        const d = deltas[best];
        held = { tone: d > 0 ? 'hike' : d < 0 ? 'cut' : 'hold', delta: d };
        out[i] = held;
    }
    return out;
}

export interface ToneRun {
    from: number;
    to: number;
    tone: Tone;
    delta: number;
}

export function toneRuns(toned: readonly Toned[]): ToneRun[] {
    if (!toned.length) return [];
    const runs: ToneRun[] = [];
    let start = 0;

    for (let i = 1; i <= toned.length; i++) {
        if (i === toned.length || toned[i].tone !== toned[start].tone) {
            runs.push({
                from: start,
                to: i - 1,
                tone: toned[start].tone,
                delta: toned[start].delta,
            });
            start = i;
        }
    }
    for (let k = 0; k < runs.length - 1; k++) runs[k].to = runs[k + 1].from;
    return runs;
}

export interface GradientStop {
    offset: number;
    color: string;
}

export function toneGradientStops(
    runs: readonly ToneRun[],
    xAt: (i: number) => number,
    colorOf: (run: ToneRun) => string
): GradientStop[] {
    if (!runs.length) return [];
    const x0 = xAt(runs[0].from);
    const x1 = xAt(runs[runs.length - 1].to);
    const span = x1 - x0;
    if (span <= 0) return [];

    const at = (px: number) => Math.min(1, Math.max(0, (px - x0) / span));
    const stops: GradientStop[] = [];

    for (const run of runs) {
        const color = colorOf(run);
        stops.push({ offset: at(xAt(run.from)), color });
        stops.push({ offset: at(xAt(run.to)), color });
    }
    return stops;
}

export function toneColor(delta: number, ink: string): string {
    return delta === 0 ? ink : binColor(delta);
}
